import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, Shield, Search, MoreHorizontal, MoreVertical,
  Ban, Building2, Mail, LogIn, Pencil, Trash2, 
  RefreshCw, CheckCircle, XCircle, UserCheck, Filter,
  AlertTriangle, User, Eye
} from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminUsers() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Dialog states
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  
  // Form states
  const [targetUser, setTargetUser] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [editForm, setEditForm] = useState({ name: '', role: '' });
  const [processing, setProcessing] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/users`, getAuthConfig());
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.tenant?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
    const matchesRole = roleFilter === 'all' || 
      (roleFilter === 'super_admin' && u.is_super_admin) ||
      (roleFilter === 'league_admin' && u.tenant_role === 'league_admin') ||
      (roleFilter === 'moderator' && u.tenant_role === 'moderator') ||
      (roleFilter === 'player' && u.tenant_role === 'player');
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  // Action handlers
  const handleSuspend = async () => {
    if (!targetUser) return;
    try {
      setProcessing(true);
      await axios.post(`${API}/platform/users/${targetUser.id}/suspend`, null, {
        ...getAuthConfig(),
        params: { reason: suspendReason || 'Suspended by administrator' }
      });
      toast.success(`User suspended`);
      setShowSuspendDialog(false);
      setSuspendReason('');
      setTargetUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    if (!targetUser) return;
    try {
      setProcessing(true);
      await axios.post(`${API}/platform/users/${targetUser.id}/reactivate`, {}, getAuthConfig());
      toast.success(`User reactivated`);
      setShowReactivateDialog(false);
      setTargetUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setProcessing(false);
    }
  };

  const handleResendInvite = async (invitedUser) => {
    setResendingInvite(invitedUser.id);
    try {
      await axios.post(`${API}/platform/invites/${invitedUser.id}/resend`, {}, getAuthConfig());
      toast.success(`Invite resent to ${invitedUser.email}`);
    } catch (error) {
      console.error('Failed to resend invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleDelete = async () => {
    if (!targetUser) return;
    try {
      setProcessing(true);
      if (targetUser.user_type === 'invited') {
        // Delete invite
        await axios.delete(`${API}/platform/invites/${targetUser.id}`, getAuthConfig());
        toast.success(`Invite cancelled`);
      } else {
        // Delete user
        await axios.delete(`${API}/platform/users/${targetUser.id}`, getAuthConfig());
        toast.success(`User deleted`);
      }
      setShowDeleteDialog(false);
      setTargetUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = async () => {
    if (!targetUser) return;
    try {
      setProcessing(true);
      await axios.patch(`${API}/platform/users/${targetUser.id}`, editForm, getAuthConfig());
      toast.success(`User updated`);
      setShowEditDialog(false);
      setTargetUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setProcessing(false);
    }
  };

  const handleImpersonate = async () => {
    if (!targetUser) return;
    try {
      setProcessing(true);
      const response = await axios.post(`${API}/platform/users/${targetUser.id}/impersonate`, {}, getAuthConfig());
      
      // Store the new session token for the impersonated user
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      toast.success(`Now logged in as ${targetUser.name}`);
      setShowImpersonateDialog(false);
      setTimeout(() => { window.location.href = '/home'; }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate');
    } finally {
      setProcessing(false);
    }
  };

  const openEditDialog = (u) => {
    setTargetUser(u);
    setEditForm({ name: u.name || '', role: u.tenant_role || '' });
    setShowEditDialog(true);
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-emerald-600 text-xs">Active</Badge>;
    if (status === 'suspended') return <Badge variant="destructive" className="text-xs">Suspended</Badge>;
    if (status === 'invited') return <Badge className="bg-amber-600 text-xs">Invited</Badge>;
    return <Badge variant="outline" className="text-xs">{status}</Badge>;
  };

  const getRoleBadge = (u) => {
    if (u.is_super_admin) return <Badge className="bg-red-600 text-xs">Super Admin</Badge>;
    if (u.tenant_role === 'league_admin') return <Badge className="bg-blue-600 text-xs">League Admin</Badge>;
    if (u.tenant_role === 'moderator') return <Badge className="bg-purple-600 text-xs">Moderator</Badge>;
    if (u.tenant_role === 'player') return <Badge variant="outline" className="text-zinc-300 text-xs">Player</Badge>;
    return <Badge variant="outline" className="text-zinc-500 text-xs">No Role</Badge>;
  };

  // Action menu for both mobile and desktop
  const UserActions = ({ u }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white h-8 w-8 p-0">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-48">
        {u.user_type !== 'invited' && (
          <DropdownMenuItem className="text-blue-400" onClick={() => navigate(`/super-admin/users/${u.id}`)}>
            <Eye className="w-4 h-4 mr-2" /> View Details
          </DropdownMenuItem>
        )}
        {u.user_type === 'invited' && (
          <DropdownMenuItem 
            className="text-blue-400" 
            onClick={() => handleResendInvite(u)}
            disabled={resendingInvite === u.id}
          >
            {resendingInvite === u.id ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              <><Mail className="w-4 h-4 mr-2" /> Resend Invite</>
            )}
          </DropdownMenuItem>
        )}
        {!u.is_super_admin && u.status === 'active' && u.user_type !== 'invited' && (
          <DropdownMenuItem className="text-emerald-400" onClick={() => { setTargetUser(u); setShowImpersonateDialog(true); }}>
            <LogIn className="w-4 h-4 mr-2" /> Login As
          </DropdownMenuItem>
        )}
        {!u.is_super_admin && u.user_type !== 'invited' && (
          <DropdownMenuItem className="text-zinc-300" onClick={() => openEditDialog(u)}>
            <Pencil className="w-4 h-4 mr-2" /> Quick Edit
          </DropdownMenuItem>
        )}
        {!u.is_super_admin && u.status === 'active' && u.user_type !== 'invited' && (
          <DropdownMenuItem className="text-amber-400" onClick={() => { setTargetUser(u); setShowSuspendDialog(true); }}>
            <Ban className="w-4 h-4 mr-2" /> Suspend
          </DropdownMenuItem>
        )}
        {!u.is_super_admin && u.status === 'suspended' && (
          <DropdownMenuItem className="text-emerald-400" onClick={() => { setTargetUser(u); setShowReactivateDialog(true); }}>
            <UserCheck className="w-4 h-4 mr-2" /> Reactivate
          </DropdownMenuItem>
        )}
        {!u.is_super_admin && (
          <>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem className="text-red-400" onClick={() => { setTargetUser(u); setShowDeleteDialog(true); }}>
              <Trash2 className="w-4 h-4 mr-2" /> {u.user_type === 'invited' ? 'Cancel Invite' : 'Delete'}
            </DropdownMenuItem>
          </>
        )}
        {u.is_super_admin && (
          <DropdownMenuItem disabled className="text-zinc-500">
            <Shield className="w-4 h-4 mr-2" /> Protected
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (!user?.is_super_admin) return null;

  return (
    <SuperAdminLayout onRefresh={loadUsers}>
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header - Title + Actions on same line */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="min-w-0">
            <h1 className="text-lg lg:text-2xl font-bold text-white">Users</h1>
            <p className="text-xs lg:text-sm text-zinc-400">
              {users.length} total • {users.filter(u => u.status === 'active').length} active • {users.filter(u => u.status === 'invited').length} invited
            </p>
          </div>
          <Button onClick={loadUsers} variant="outline" size="sm" className="text-zinc-300 flex-shrink-0">
            <RefreshCw className={`w-4 h-4 lg:mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">Refresh</span>
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search by name, email, or tenant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-800 text-white text-sm"
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 sm:w-36 bg-zinc-900 border-zinc-800 text-white text-xs sm:text-sm">
                <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="invited">Invited</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>

            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-32 sm:w-36 bg-zinc-900 border-zinc-800 text-white text-xs sm:text-sm">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="league_admin">League Admin</SelectItem>
                <SelectItem value="moderator">Moderator</SelectItem>
                <SelectItem value="player">Player</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-xs text-zinc-400">Showing {filteredUsers.length} of {users.length} users</p>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12 text-zinc-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            No users found
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-zinc-900">
                    <TableHead className="text-zinc-400">User</TableHead>
                    <TableHead className="text-zinc-400">Role</TableHead>
                    <TableHead className="text-zinc-400">Tenant</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map(u => (
                    <TableRow key={u.id} className="border-zinc-800 hover:bg-zinc-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt={u.name} className="w-9 h-9 rounded-full" />
                          ) : (
                            <div 
                              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: u.color || '#3b82f6' }}
                            >
                              {u.name?.[0]?.toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-white text-sm">{u.name}</p>
                            <p className="text-xs text-zinc-400">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(u)}</TableCell>
                      <TableCell>
                        {u.tenant ? (
                          <span className="text-sm text-zinc-300 flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {u.tenant.name}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-500">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(u.status)}</TableCell>
                      <TableCell className="text-right">
                        <UserActions u={u} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-2">
              {filteredUsers.map(u => (
                <div key={u.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.name} className="w-10 h-10 rounded-full flex-shrink-0" />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                        style={{ backgroundColor: u.color || '#3b82f6' }}
                      >
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-white text-sm truncate">{u.name}</p>
                          <p className="text-xs text-zinc-400 truncate">{u.email}</p>
                        </div>
                        <UserActions u={u} />
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {getRoleBadge(u)}
                        {getStatusBadge(u.status)}
                        {u.tenant && (
                          <span className="text-xs text-zinc-400 flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {u.tenant.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="w-5 h-5 text-amber-400" /> Suspend User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4 text-sm">Suspend <strong className="text-white">{targetUser?.name}</strong>?</p>
            <Label className="text-zinc-300 text-sm">Reason (optional)</Label>
            <Textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason..."
              className="mt-2 bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)} className="text-zinc-300 w-full sm:w-auto">Cancel</Button>
            <Button variant="destructive" onClick={handleSuspend} disabled={processing} className="w-full sm:w-auto">
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReactivateDialog} onOpenChange={setShowReactivateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-400" /> Reactivate User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 text-sm">Reactivate <strong className="text-white">{targetUser?.name}</strong>?</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowReactivateDialog(false)} className="text-zinc-300 w-full sm:w-auto">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" onClick={handleReactivate} disabled={processing}>
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Reactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4 text-sm">Delete <strong className="text-white">{targetUser?.name}</strong>?</p>
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-xs text-red-300">
              <p className="font-medium">⚠️ This cannot be undone</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="text-zinc-300 w-full sm:w-auto">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={processing} className="w-full sm:w-auto">
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil className="w-5 h-5" /> Edit User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            {targetUser?.tenant && (
              <div>
                <Label className="text-zinc-300 text-sm">Role in {targetUser.tenant.name}</Label>
                <Select value={editForm.role} onValueChange={(value) => setEditForm({...editForm, role: value})}>
                  <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="league_admin">League Admin</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="player">Player</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="text-zinc-300 w-full sm:w-auto">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" onClick={handleEdit} disabled={processing}>
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showImpersonateDialog} onOpenChange={setShowImpersonateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <LogIn className="w-5 h-5 text-emerald-400" /> Login as User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-300 mb-4 text-sm">Log in as <strong className="text-white">{targetUser?.name}</strong>?</p>
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-xs text-zinc-300 space-y-1">
              <p>• You'll see the app as this user</p>
              <p>• Actions will be logged</p>
              <p>• Log out to return</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowImpersonateDialog(false)} className="text-zinc-300 w-full sm:w-auto">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" onClick={handleImpersonate} disabled={processing}>
              {processing && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />} Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
}
